/**
 * ArtefactViewer Component
 *
 * Displays individual artefacts with type-specific viewers and common actions.
 * Refactored to use specialized content viewers for better maintainability.
 */

import React from "react";
import { Separator } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";

import { Artefact } from "@/types/artefacts";

import {
  CodeArtefactViewer,
  DocumentArtefactViewer,
  TableArtefactViewer,
  HTMLArtefactViewer,
  GraphArtefactViewer,
} from "./ArtefactViewers";
import { ArtefactHeader } from "./ArtefactComponents";

interface ArtefactViewerProps {
  artefact: Artefact;
}

const getContentViewer = (type: string) => {
  switch (type) {
    case "code":
      return CodeArtefactViewer;
    case "document":
      return DocumentArtefactViewer;
    case "table":
      return TableArtefactViewer;
    case "html":
      return HTMLArtefactViewer;
    case "graph":
      return GraphArtefactViewer;
    default:
      return null;
  }
};

export const ArtefactViewer: React.FC<ArtefactViewerProps> = ({ artefact }) => {
  const ContentViewer = getContentViewer(artefact.type);

  const renderArtefactContent = () => {
    if (ContentViewer) {
      return <ContentViewer artefact={artefact} />;
    }

    // Fallback for unsupported types
    return (
      <div className="mt-4">
        <div className="text-center text-default-500 py-8 border border-dashed border-default-200 rounded-lg">
          <Icon className="mx-auto mb-2" height={48} icon="solar:code-square-bold" width={48} />
          <p>Unsupported artefact type: {artefact.type}</p>
          <p className="text-xs mt-2">Advanced viewers coming soon</p>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full overflow-auto">
      <div className="p-2">
        <ArtefactHeader artefact={artefact} />
      </div>

      <Separator className="my-3" />

      <div className="px-2 flex-1">{renderArtefactContent()}</div>
    </div>
  );
};
